import { CameraAnimation } from "@/components/r3f/animations/camera-animation";
import { CameraSync } from "@/components/r3f/utils/camera-sync";
import { SuspenseLoadNotifier } from "@/components/r3f/utils/suspense-load-notifier";
import { selectShouldSyncCamerasRotation } from "@/store/modes/walk-mode-slice";
import { useAppSelector } from "@/store/store-hooks";
import type { SceneFilter } from "@/types/scene-filter";
import type { SetCameraView } from "@/utils/cam-view";
import { ForwardMovement } from "@faro-lotv/lotv";
import {
  Suspense,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
  type JSX,
  type RefObject,
} from "react";
import type { Camera, Quaternion, Vector3 } from "three";
import { useCurrentArea } from "../mode-data-context";
import type { CurrentScene } from "../mode-selectors";
import type { InitialAnimationInfo } from "./data-dispatch/flywalk-animation";
import {
  FlyWalkDataDispatch,
  INITIAL_DATA_DISPATCH_RESULT,
  type DataDispatchResult,
} from "./data-dispatch/flywalk-data-dispatch";
import { useDowncastObjects } from "./data-dispatch/flywalk-renderables";
import {
  INITIAL_FLYWALK_ANIMATION_DATA,
  type FlyWalkAnimationData,
} from "./data-dispatch/use-flywalk-animation";
import type { MoveCameraToCbk } from "./data-dispatch/use-move-camera-to";
import { FlyWalkControls } from "./flywalk-controls";
import { useOnPanoClicked, type OnWaypointClicked } from "./flywalk-scene";
import type { FlyWalkWaypoint } from "./flywalk-types";
import { FlyWalkRenderers } from "./renderers/flywalk-renderers";
import { FlyWalkRenderingPipeline } from "./rendering-pipeline/flywalk-rendering-pipeline";
import { FlyWalkTools } from "./tools/flywalk-tools";
import { ToolsAcquaintanceContextProvider } from "./tools/tools-acquaintance-context";
import type { SingleViewCameras } from "./use-fly-split-scene-cameras";
import {
  useOrthoCameraForImagesSplitView,
  usePerspectiveCameraForNonImagesSplitView,
} from "./use-orthocamera-for-images";

export type FlySplitViewActions = {
  /** Callback issued when a waypoint is clicked in the minimap */
  onPanoClickedInMinimap: OnWaypointClicked;
  /** Callback issued when the camera should be animated to a new position */
  onCameraMovedTo: MoveCameraToCbk;
  /** Callback issued when the camera view should be set */
  setCameraView: SetCameraView;
};

type FlySplitViewProps = {
  /** The scene to render in this view */
  currentScene: CurrentScene;
  /** The scene filter to use for this view */
  sceneFilter: SceneFilter;
  /** Whether this view is the secondary view (right side) or not (left side) */
  isSecondaryView: boolean;
  /** Callback issued when a waypoint is clicked */
  onWaypointClicked(waypoint: FlyWalkWaypoint): void;
  /** A handle on actions that can be executed on the split view */
  actions?: RefObject<FlySplitViewActions | null>;
  /** The cameras used to render this view*/
  cameras: SingleViewCameras;
  /** The camera the user is interacting with */
  activeCamera: Camera;
  /** Callback to set the camera the user is interacting with */
  setActiveCamera?(camera: Camera): void;
  /** Callback issued when a camera movement via controls finishes */
  onCameraMovedViaControls?(pos: Vector3): void;
  /** Callback issued when an animation finishes */
  onAnimationEnded?(): void;
};

/** @returns a component suitable to render one of the two views shown in splitscreen mode */
export function FlySplitView({
  currentScene,
  sceneFilter,
  isSecondaryView,
  onWaypointClicked,
  actions,
  cameras,
  activeCamera,
  setActiveCamera,
  onCameraMovedViaControls,
  onAnimationEnded,
}: FlySplitViewProps): JSX.Element {
  const area = useCurrentArea();

  const [dataDispatchResult, setDataDispatchResult] =
    useState<DataDispatchResult>(INITIAL_DATA_DISPATCH_RESULT);

  const [animationData, setAnimationData] = useState<FlyWalkAnimationData>(
    INITIAL_FLYWALK_ANIMATION_DATA,
  );

  // 'dataDispatchResult' contains the project data that should be dispatched to this scene for rendering,
  // according to the user selection and the scene filter
  const {
    main,
    mainObject,
    overlayObject,
    annotations,
    measurements,
    imageType,
  } = dataDispatchResult;

  usePerspectiveCameraForNonImagesSplitView(main, cameras);

  // 'animationData' contains the current animation state and the previous main object for rendering.
  // Notably, the 'currentSceneFilter' below is different from the 'flyWalkSceneFilter' above.
  // The latter refers to what the user has selected in the UI. The former refers to what the
  // rendering pipeline is ready to take as input (as opposed to an animation)
  const {
    flyWalkAnimation,
    currentSceneFilter,
    previousMain,
    previousMainObject,
    endAnimation,
    moveCameraTo,
    setCameraView,
  } = animationData;

  useOrthoCameraForImagesSplitView(
    main,
    flyWalkAnimation,
    !previousMain,
    cameras,
  );

  // Retrieves the current renderable objects with their correct types.
  const renderables = useDowncastObjects(
    mainObject,
    overlayObject,
    previousMainObject,
    currentSceneFilter,
    currentScene.activeSheets,
    isSecondaryView,
  );

  const initialAnimationInfo = useRef<InitialAnimationInfo | null>({
    canSkipInitialTransition: true,
  });

  const { onPanoClicked, onPanoClickedInMinimap } = useOnPanoClicked(
    currentSceneFilter,
    onWaypointClicked,
  );

  const onCameraMovedTo = useCallback(
    (pos: Vector3, quaternion?: Quaternion, zoomLevel?: number) => {
      moveCameraTo(pos, quaternion, zoomLevel);
    },
    [moveCameraTo],
  );

  useImperativeHandle(actions, () => ({
    onPanoClickedInMinimap,
    onCameraMovedTo,
    setCameraView,
  }));

  const shouldSyncRotation = useAppSelector(selectShouldSyncCamerasRotation);

  const onAnimationFinished = useCallback(() => {
    endAnimation();
    onAnimationEnded?.();
  }, [endAnimation, onAnimationEnded]);

  return (
    <ToolsAcquaintanceContextProvider>
      <Suspense
        fallback={<SuspenseLoadNotifier withSnapshotRenderer={false} />}
      >
        <FlyWalkDataDispatch
          flyWalkSceneFilter={sceneFilter}
          currentScene={currentScene}
          currentArea={area}
          initialAnimationInfo={initialAnimationInfo}
          setDataDispatchResult={setDataDispatchResult}
          setAnimationData={setAnimationData}
          defaultOrthoCamera={cameras.orthoCamera}
          isSecondaryView={isSecondaryView}
        />
      </Suspense>
      <FlyWalkRenderers
        renderables={renderables}
        sheets={[]}
        panos={currentScene.panos}
        paths={currentScene.paths}
        sheetForElevation={currentScene.activeSheetFor2DNavigation}
        annotations={annotations}
        measurements={measurements}
        animation={flyWalkAnimation?.renderingAnimation ?? null}
        sceneFilter={currentSceneFilter}
        imageType={imageType}
        isOverview={false}
        onPanoClicked={onPanoClicked}
        onWalkToPosition={flyWalkAnimation ? undefined : moveCameraTo}
      />
      <FlyWalkControls
        imageType={imageType}
        animation={flyWalkAnimation}
        target={undefined}
        forwardMovement={ForwardMovement.Walk}
        clickableByControls={[]}
        setActiveCamera={setActiveCamera}
        onCameraMovedViaControls={onCameraMovedViaControls}
      />
      <FlyWalkRenderingPipeline
        pointCloud={renderables.pointCloud}
        cadModel={renderables.cadModel}
        sceneFilter={currentSceneFilter}
        animation={flyWalkAnimation?.renderingAnimation ?? null}
        endAnimation={onAnimationFinished}
      />
      <FlyWalkTools
        sceneFilter={currentSceneFilter}
        renderables={renderables}
        isAnimating={!!flyWalkAnimation}
        imageType={imageType}
        paths={currentScene.paths}
      />
      {flyWalkAnimation?.cameraAnimation && (
        <CameraAnimation
          {...flyWalkAnimation.cameraAnimation}
          onAnimationFinished={onAnimationFinished}
        />
      )}
      {flyWalkAnimation?.targetCameraAnimation && (
        <CameraAnimation
          {...flyWalkAnimation.targetCameraAnimation}
          syncControlsWithCameraAtEnd={false}
        />
      )}
      <CameraSync
        source={activeCamera}
        shouldSync={shouldSyncRotation}
        shouldSyncFov
      />
    </ToolsAcquaintanceContextProvider>
  );
}
